import { trpcSwr } from '../../../../api/trpc';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import tableStyle from '../../../row.module.css';
import { getCurrencySymbol, toMoney } from '../../../../utils/common.utils';
import {
  CleanCustomsDeliveryOrderGoodToll,
  RbacObject,
} from '../../../../export-types/cleaned-types';
import { Controller, useForm } from 'react-hook-form';
import { useRbac } from '../../../common/Rbac';
import { InputWrapper } from '../../../common/input-wrapper';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Checkbox } from '../../../common/checkbox';
import { Select } from '../../../common/select.component';
import { required } from '../../../../utils/validations';
import Whisper from 'rsuite/Whisper';
import Tooltip from 'rsuite/Tooltip';

type Form = {
  cargoPlaces?: number | null;
  packagePrice?: number | null;
  customsFeePrice?: number | null;
  deliveryMethodId?: string;
  additionalCustomsFeePrice?: number | null;
  additionalSupportPrice?: number | null;
  supportId?: string;
  individualLogisticPrice?: number | null;
  vat?: number;
};

const IndividualTariffValue = 'individual';

const Cell: React.FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  return (
    <div
      className={classNames(
        'col full-text   py-1 px-2 align-items-center ',
        className,
      )}
    >
      {children}
    </div>
  );
};

const Row: React.FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  return (
    <div
      className={classNames(
        'position-relative row g-0 bg-transparent border-bottom  p-0   h-100 w-100',
        tableStyle.tableRow,
        className,
      )}
    >
      {children}
    </div>
  );
};

export const CustomsDeliveryCalculationTable = ({ id }: { id: string }) => {
  const { data: priceData, mutate } =
    trpcSwr.customsDeliveryOrderPriceData.getData.useSWR(
      {
        id,
      },
      {
        onSuccess: (data) => {
          setApplyMarking(!!data?.trueSignApplyMarkingPrice);
          setOrderMarking(!!data?.trueSignOrderMarkingPrice);
          mutateLogs();
        },
      },
    );
  const { mutate: mutateLogs } = trpcSwr.customsDelivery.getOrderLogs.useSWR({
    id: id,
  });
  const { data: order } = trpcSwr.customsDelivery.getOne.useSWR({
    id: id,
  });

  const { trigger: updateData } =
    trpcSwr.customsDeliveryOrderPriceData.updateData.useSWRMutation();
  const { hasPermission } = useRbac();

  const [applyMarking, setApplyMarking] = React.useState(false);
  const [orderMarking, setOrderMarking] = React.useState(false);

  const canEdit =
    hasPermission(RbacObject.CustomsDelivery, 'write') &&
    [
      'calculationReady',
      'protectCalculation',
      'prepareDocs',
      'prepareInvoice',
      'declarant',
      'waitingForPayment',
      'buyOut',
      'inWork',
      'inStock',
    ].includes(order?.status || '');
  const { handleSubmit, control, reset, watch } = useForm<Form>({});

  const deliveryMethodId = watch('deliveryMethodId');

  const tollsData = useMemo<
    Array<CleanCustomsDeliveryOrderGoodToll & { code: string | null }>
  >(() => {
    return (
      order?.goods
        .map((good) =>
          (good.tolls || []).map((toll) => ({
            ...toll,
            code: good.codeTariff,
          })),
        )
        .flat(1) || []
    );
  }, [order]);

  const isSaved = useMemo(() => {
    return (
      (priceData?.customsDeliveryMethod ||
        priceData?.individualLogisticPrice) &&
      priceData.vatPrice !== undefined
    );
  }, [priceData]);
  const [isEditing, setIsEditing] = React.useState(false);

  const totalLogistic = useMemo(() => {
    return (
      (priceData?.operationsPrice || 0) +
      (priceData?.logisticPrice || 0) +
      (priceData?.cargoPlacesPrice || 0) +
      (priceData?.packagePrice || 0) +
      (priceData?.insurancePrice || 0) +
      (priceData?.individualLogisticPrice || 0)
    );
  }, [priceData]);

  const totalCustoms = useMemo(() => {
    return (
      (priceData?.vatPrice || 0) +
      (priceData?.customsDutyPrice || 0) +
      (priceData?.declarationPrice || 0)
    );
  }, [priceData]);

  const totalInvoice = useMemo(() => {
    return (
      (priceData?.bankCommissionPrice || 0) +
      (priceData?.invoicePrice || 0) +
      (priceData?.bankCommissionConstant || 0)
    );
  }, [priceData]);

  const { data: deliveryMethods } =
    trpcSwr.customsDeliveryMethod.getList.useSWR(
      {
        supplierId: order?.supplier?.id,
        onlyActive: true,
      },
      { isDisabled: !isEditing },
    );
  const { data: supports } = trpcSwr.customsDeliverySupport.getList.useSWR(
    {
      onlyActive: true,
    },
    { isDisabled: !isEditing },
  );
  useEffect(() => {
    if (priceData) {
      reset({
        ...priceData,
        vat: priceData?.vat || 0,
        deliveryMethodId: priceData.individualLogisticPrice
          ? IndividualTariffValue
          : priceData?.customsDeliveryMethod?.id,
        supportId: priceData?.customsDeliverySupport?.id ?? undefined,
      });
    }
  }, [reset, priceData]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
      return;
    }
    setIsEditing(!isSaved);
  }, [canEdit, isSaved]);
  return (
    <div className={'d-flex flex-column gap-2'}>
      <form
        id="customsDeliveryPriceData"
        onSubmit={handleSubmit(async (data) => {
          const processedData = {
            ...data,
          };
          await updateData({
            id,
            data: {
              deliveryMethodId:
                processedData.deliveryMethodId !== IndividualTariffValue
                  ? processedData.deliveryMethodId
                  : undefined,
              packagePrice: processedData.packagePrice || undefined,
              cargoPlaces: processedData.cargoPlaces || undefined,
              vat: processedData.vat,
              applyMarking,
              orderMarking,
              additionalSupportPrice:
                processedData.additionalSupportPrice || undefined,
              additionalCustomsFee:
                processedData.additionalCustomsFeePrice || undefined,
              supportId: processedData.supportId || undefined,
              individualLogisticPrice:
                processedData.deliveryMethodId !== IndividualTariffValue
                  ? 0
                  : processedData.individualLogisticPrice || undefined,
            },
          })
            .then(() => {
              toast.success('Расчет обновлен');
            })
            .catch((e) => toast.error(e));
          mutate();
          setIsEditing(false);
        })}
      >
        <div className="justify-content-between">
          <div className={'d-flex gap-2 align-items-center'}>
            <div className="fs-18 fw-600 ">Расчет стоимости услуг</div>
            {canEdit ? (
              <>
                {isSaved && !isEditing && (
                  <button
                    type="button"
                    className="btn btn-light mb-2 mleft-auto"
                    disabled={!isSaved}
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    Изменить
                  </button>
                )}
                {isEditing && (
                  <button
                    type="submit"
                    className="btn btn-success mb-2 mleft-auto"
                  >
                    Сохранить
                  </button>
                )}
              </>
            ) : null}
          </div>

          <div
            className={classNames(
              'mb-3 border w-100 ',
              tableStyle.rowContainer,
            )}
          >
            <Row>
              <Cell className={'text-left fw-600'}>
                {moment(priceData?.updatedAt || new Date()).format(
                  'DD-MM-YYYY',
                )}{' '}
                {getCurrencySymbol('cny')} -{' '}
                {toMoney((priceData?.cnyRubRateCbr || 0).toFixed(2), 'rub')}{' '}
                {getCurrencySymbol('usd')} -{' '}
                {toMoney((priceData?.usdRubRateCbr || 0).toFixed(2), 'rub')}
                <Whisper
                  placement="right"
                  trigger="hover"
                  speaker={
                    <Tooltip
                      className="fs-14 text-start lh-sm p-2"
                      style={{ maxWidth: 293 }}
                    >
                      Используется наценка на курс ЦБ <br />
                      {toMoney(
                        (
                          ((priceData?.cnyRubRateCbr || 0) / 100) *
                          (100 - (priceData?.cnyRubCommission || 0))
                        ).toFixed(2),
                        'rub',
                      )}{' '}
                      + {priceData?.cnyRubCommission || 0}% ,
                      {toMoney(
                        (
                          ((priceData?.usdRubRateCbr || 0) / 100) *
                          (100 - (priceData?.usdRubCommission || 0))
                        ).toFixed(2),
                        'rub',
                      )}{' '}
                      + {priceData?.usdRubCommission || 0}%
                    </Tooltip>
                  }
                >
                  <i className="ms-2 bi bi-question-circle" />
                </Whisper>
              </Cell>
              <Cell className={'col-2 text-end'}>Итого</Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>Погрузо-разгрузочные работы</Cell>
              <Cell className={'text-center'}>{priceData?.pRRRate} $/м3</Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.operationsPrice || 0, 'usd')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>
                Логистика <span className="color-red"> *</span>
              </Cell>
              <Cell className={'col-3 text-center'}>
                <Controller
                  name="deliveryMethodId"
                  control={control}
                  rules={{ ...required }}
                  render={({ field }) => (
                    <>
                      {isEditing ? (
                        <Select
                          {...field}
                          options={[
                            ...(deliveryMethods || []).map((method) => {
                              return {
                                value: method.id,
                                label: method.name,
                              };
                            }),
                            {
                              value: IndividualTariffValue,
                              label: 'Индивидуальный тариф',
                            },
                          ]}
                          placeholder="Способ"
                        />
                      ) : priceData?.individualLogisticPrice ? (
                        'Индивидуальный тариф'
                      ) : (
                        priceData?.customsDeliveryMethod?.name
                      )}
                    </>
                  )}
                />
              </Cell>
              <Cell className={'col-4 text-end'}>
                {deliveryMethodId === IndividualTariffValue ? (
                  <Controller
                    name="individualLogisticPrice"
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <InputWrapper
                          error={fieldState.error?.message}
                          className={classNames(
                            'text-end',
                            isEditing && 'input-group',
                          )}
                          required={true}
                        >
                          {isEditing ? (
                            <>
                              <input
                                className="form-control"
                                type="number"
                                min={0.01}
                                step={0.01}
                                defaultValue={field.value || undefined}
                                onChange={(e) => {
                                  field.onChange(+e.target.value);
                                }}
                              />
                              <span className="input-group-text">$</span>
                            </>
                          ) : (
                            toMoney(field.value || 0, 'usd')
                          )}
                        </InputWrapper>
                      );
                    }}
                  />
                ) : (
                  toMoney(priceData?.logisticPrice || 0, 'usd')
                )}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5 '}>
                Количество грузовых мест
                <Whisper
                  placement="right"
                  trigger="hover"
                  speaker={
                    <Tooltip
                      className="fs-14 text-start lh-sm p-2"
                      style={{ maxWidth: 293 }}
                    >
                      Количество транспортных коробок, которые будут грузится в
                      контейнер или авто
                    </Tooltip>
                  }
                >
                  <i className="ms-2 bi bi-question-circle" />
                </Whisper>
              </Cell>
              <Cell className={'col-3 text-center'}>
                <Controller
                  name="cargoPlaces"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWrapper
                        error={fieldState.error?.message}
                        className={classNames(
                          isEditing && 'input-group',
                          'text-center',
                        )}
                      >
                        {isEditing ? (
                          <>
                            <input
                              className="form-control"
                              type="number"
                              defaultValue={field.value || undefined}
                              onChange={(e) => {
                                field.onChange(+e.target.value);
                              }}
                            />
                            <span className="input-group-text">/шт</span>
                          </>
                        ) : (
                          field.value
                        )}
                      </InputWrapper>
                    );
                  }}
                />
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.cargoPlacesPrice || 0, 'usd')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-8'}>Упаковка (обрешетка,уголки)</Cell>
              <Cell className={'col-4 text-end'}>
                <Controller
                  name="packagePrice"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWrapper
                        error={fieldState.error?.message}
                        className={classNames(
                          'text-end',
                          isEditing && 'input-group',
                        )}
                        required={true}
                      >
                        {isEditing ? (
                          <>
                            <input
                              className="form-control"
                              type="number"
                              min={0.01}
                              step={0.01}
                              defaultValue={field.value || undefined}
                              onChange={(e) => {
                                field.onChange(+e.target.value);
                              }}
                            />
                            <span className="input-group-text">$</span>
                          </>
                        ) : (
                          toMoney(field.value || 0, 'usd')
                        )}
                      </InputWrapper>
                    );
                  }}
                />{' '}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>Гарантия утраты и порчи</Cell>
              <Cell className={'text-center'}>
                {priceData?.insuranceRate} %
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.insurancePrice?.toFixed(2) || 0, 'usd')}
              </Cell>
            </Row>
            <Row className={'fw-600'}>
              <Cell className={'col-8'}>Итого логистика</Cell>
              <Cell className={'text-end'}>
                {toMoney(totalLogistic.toFixed(2), 'usd')}
              </Cell>
            </Row>

            {tollsData.map((item, index) => {
              return (
                <Row key={index}>
                  <Cell className={'col-5 '}>
                    Таможенная пошлина {item.code}
                  </Cell>
                  <Cell className={'col-3 text-center'}>{item?.rate} </Cell>
                  <Cell className={'col-4 text-end'}>
                    {toMoney((item?.total || 0).toFixed(2), 'rub')}
                  </Cell>
                </Row>
              );
            })}

            <Row>
              <Cell className={'col-8'}>Дополнительные там. пошлины</Cell>
              <Cell className={'col-4 text-end'}>
                <Controller
                  name="additionalCustomsFeePrice"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWrapper
                        error={fieldState.error?.message}
                        className={classNames(
                          'text-end',
                          isEditing && 'input-group',
                        )}
                        required={true}
                      >
                        {isEditing ? (
                          <>
                            <input
                              className="form-control"
                              type="number"
                              min={0.01}
                              step={0.01}
                              defaultValue={field.value || undefined}
                              onChange={(e) => {
                                field.onChange(+e.target.value);
                              }}
                            />
                            <span className="input-group-text">
                              {getCurrencySymbol('rub')}
                            </span>
                          </>
                        ) : (
                          toMoney(field.value || 0, 'rub')
                        )}
                      </InputWrapper>
                    );
                  }}
                />{' '}
              </Cell>
            </Row>

            <Row className={'fw-600'}>
              <Cell className={'col-8 '}>Итого таможенная пошлина</Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney((priceData?.customsFeePrice || 0).toFixed(2), 'rub')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>
                НДС <span className="color-red"> *</span>
              </Cell>
              <Cell className={'col-3 text-center'}>
                <Controller
                  name="vat"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWrapper
                        error={fieldState.error?.message}
                        required={true}
                      >
                        {isEditing ? (
                          <select
                            required={true}
                            onChange={(v) => {
                              field.onChange(+v.target.value);
                            }}
                            defaultValue={field.value || undefined}
                            className="w-100 rounded border border-gray-300 p-2 text-left fs-14 "
                          >
                            {[0, 10, 22].map((item, index) => {
                              return (
                                <option
                                  value={item}
                                  key={index}
                                  className="mb-1  bg-white"
                                >
                                  {item}%
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          field.value + '%'
                        )}
                      </InputWrapper>
                    );
                  }}
                />
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.vatPrice?.toFixed(2) || 0, 'rub')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>Таможенный сбор</Cell>
              <Cell className={'text-center'}></Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.customsDutyPrice || 0, 'rub')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>Процедуры таможенной очистки</Cell>
              <Cell className={'text-center'}></Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.declarationPrice || 0, 'rub')}
              </Cell>
            </Row>
            <Row className={'fw-600'}>
              <Cell className={'col-9'}>Итого таможенное оформление</Cell>
              <Cell className={'text-end'}>
                {toMoney(totalCustoms.toFixed(2), 'rub')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>Инвойс</Cell>
              <Cell className={'text-center'}></Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.invoicePrice || 0, 'cny')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>
                Банковская комиссия за перевод иностранной валюты
              </Cell>
              <Cell className={'text-center'}>
                {priceData?.bankCommissionRate}%
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.bankCommissionPrice || 0, 'cny')}
              </Cell>
            </Row>
            {priceData?.bankCommissionConstant && (
              <Row>
                <Cell className={'col-8'}>Банковская комиссия</Cell>

                <Cell className={'col-4 text-end'}>
                  {priceData?.bankCommissionConstant
                    ? toMoney(
                        priceData.bankCommissionConstant.toFixed(2),
                        'cny',
                      )
                    : ''}
                </Cell>
              </Row>
            )}

            <Row className={'fw-600'}>
              <Cell className={'col-10'}>Инвойс Итого</Cell>
              <Cell className={'text-end'}>
                {toMoney(totalInvoice.toFixed(2), 'cny')}
              </Cell>
            </Row>

            <Row>
              <Cell className={'col-5'}>Сопровождение поставки</Cell>
              <Cell className={'col-3 text-center'}>
                <Controller
                  name="supportId"
                  control={control}
                  render={({ field }) => (
                    <>
                      {isEditing ? (
                        <Select
                          {...field}
                          options={(supports || []).map((method) => {
                            return {
                              value: method.id,
                              label: method.name,
                            };
                          })}
                          placeholder="Сопровождение"
                        />
                      ) : (
                        priceData?.customsDeliverySupport?.name
                      )}
                    </>
                  )}
                />
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(
                  priceData?.customsDeliverySupportPrice?.toFixed(2) || 0,
                  'rub',
                )}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-8'}>
                Сопровождение поставки - доп. расходы
              </Cell>
              <Cell className={'col-4 text-end'}>
                <Controller
                  name="additionalSupportPrice"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <InputWrapper
                        error={fieldState.error?.message}
                        className={classNames(
                          'text-end',
                          isEditing && 'input-group',
                        )}
                        required={true}
                      >
                        {isEditing ? (
                          <>
                            <input
                              className="form-control"
                              type="number"
                              min={0.01}
                              step={0.01}
                              defaultValue={field.value || undefined}
                              onChange={(e) => {
                                field.onChange(+e.target.value);
                              }}
                            />
                            <span className="input-group-text">
                              {getCurrencySymbol('rub')}
                            </span>
                          </>
                        ) : (
                          toMoney(field.value || 0, 'rub')
                        )}
                      </InputWrapper>
                    );
                  }}
                />{' '}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5 d-flex gap-2'}>
                ЧЗ заказ маркировки{' '}
                {isEditing && (
                  <Checkbox
                    onChange={({ target: { checked } }) => {
                      setOrderMarking(checked);
                      setIsEditing(true);
                    }}
                    value={orderMarking}
                  />
                )}
              </Cell>
              <Cell className={'text-center'}>
                {priceData?.trueSignOrderMarkingRate} р/шт
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.trueSignOrderMarkingPrice || 0, 'rub')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5 d-flex gap-2'}>
                ЧЗ нанесение маркировки{' '}
                {isEditing && (
                  <Checkbox
                    onChange={({ target: { checked } }) => {
                      setApplyMarking(checked);
                      setIsEditing(true);
                    }}
                    value={applyMarking}
                  />
                )}
              </Cell>
              <Cell className={'text-center'}>
                {priceData?.trueSignApplyMarkingRate} р/шт
              </Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.trueSignApplyMarkingPrice || 0, 'rub')}
              </Cell>
            </Row>
            <Row className={'fw-600'}>
              <Cell className={'col-5'}>Итого стоимость поставки</Cell>
              <Cell></Cell>
              <Cell className={'col-4 text-end'}>
                {toMoney(priceData?.totalPrice?.toFixed(2) || 0, 'rub')}
              </Cell>
            </Row>
            <Row>
              <Cell className={'col-5'}>Себестоимость 1 единицы товара</Cell>
              <Cell></Cell>
              <Cell className={'text-end'}>
                {toMoney(
                  priceData?.totalPrice
                    ? (priceData?.totalPrice / (order?.amount || 1)).toFixed(2)
                    : 0,
                  'rub',
                )}
              </Cell>
            </Row>
          </div>
        </div>
      </form>
    </div>
  );
};
